import {
    type AccountInfo,
    BrowserAuthError,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { Button, Center, Flex, Text, useMantineTheme } from "@mantine/core";
import { type ReactNode, useEffect, useState } from "react";
import { scopesNeeded } from "src/utils/authConfig";

// Get auth config from environment - required, no fallbacks
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID;
const tenantId = import.meta.env.VITE_AUTH_TENANT_ID;

if (!clientId || !tenantId) {
    throw new Error(
        "Copilot authentication configuration missing. Please ensure VITE_AUTH_CLIENT_ID and VITE_AUTH_TENANT_ID environment variables are set.",
    );
}

// Copilot-specific MSAL configuration for iframe usage
const msalConfig = {
    auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: "localStorage" as const,
        storeAuthStateInCookie: true,
    },
    system: {
        allowRedirectInIframe: false,
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
        asyncPopups: true,
    },
};

// Initialize MSAL instance for copilot
const msalInstance = new PublicClientApplication(msalConfig);

interface CopilotAuthProviderProps {
    children: ReactNode;
    authMode?: "sso" | "popup" | "inherit";
    loginHint?: string;
    sid?: string;
}

export function CopilotAuthProvider({
    children,
    authMode = "popup",
    loginHint,
    sid,
}: CopilotAuthProviderProps) {
    const theme = useMantineTheme();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [_account, setAccount] = useState<AccountInfo | null>(null);

    useEffect(() => {
        const initAuth = async () => {
            try {
                await msalInstance.initialize();

                // Check if we already have an account
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    const account = accounts[0];
                    msalInstance.setActiveAccount(account);
                    setAccount(account);

                    // Validate the cached token is still usable
                    try {
                        await msalInstance.acquireTokenSilent({
                            scopes: scopesNeeded,
                            account: account,
                            forceRefresh: false,
                        });
                        // Token is valid, user is authenticated
                        setIsAuthenticated(true);
                        setIsLoading(false);
                        return;
                    } catch (tokenError) {
                        if (tokenError instanceof InteractionRequiredAuthError) {
                            // Token expired and needs re-authentication
                            console.info(
                                "[Copilot] Cached token expired, user needs to re-authenticate",
                            );
                            // Don't set error yet - show login button instead
                        } else {
                            // Non-critical error (e.g., network), try to proceed
                            console.warn(
                                "[Copilot] Token validation failed, proceeding:",
                                tokenError,
                            );
                            setIsAuthenticated(true);
                            setIsLoading(false);
                            return;
                        }
                    }
                }

                // Try SSO silent authentication
                if (authMode === "sso" || authMode === "inherit") {
                    try {
                        const ssoRequest = {
                            scopes: scopesNeeded,
                            ...(loginHint && { loginHint }),
                            ...(sid && { sid }),
                        };

                        const response = await msalInstance.ssoSilent(ssoRequest);
                        msalInstance.setActiveAccount(response.account);
                        setAccount(response.account);
                        setIsAuthenticated(true);
                    } catch (ssoError) {
                        console.error("SSO Silent failed:", ssoError);

                        if (ssoError instanceof InteractionRequiredAuthError) {
                            setError("Authentication required. Please sign in.");
                        } else {
                            setError("SSO authentication failed.");
                        }
                    }
                }
            } catch (error) {
                console.error("Auth initialization error:", error);
                setError("Failed to initialize authentication.");
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();

        // Listen for authentication messages from parent
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === "cts-copilot-auth") {
                const { loginHint, sid } = event.data;

                try {
                    const response = await msalInstance.ssoSilent({
                        scopes: scopesNeeded,
                        loginHint,
                        sid,
                    });
                    msalInstance.setActiveAccount(response.account);
                    setAccount(response.account);
                    setIsAuthenticated(true);
                    setError(null);
                } catch (error) {
                    console.error("Parent auth sync failed:", error);
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [authMode, loginHint, sid]);

    const handleLogin = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const loginRequest = {
                scopes: scopesNeeded,
                ...(loginHint && { loginHint }),
                prompt: "select_account",
            };

            const response = await msalInstance.loginPopup(loginRequest);
            msalInstance.setActiveAccount(response.account);
            setAccount(response.account);
            setIsAuthenticated(true);

            // Notify parent of successful login
            window.parent.postMessage(
                {
                    type: "cts-copilot-auth-success",
                    account: {
                        username: response.account.username,
                        name: response.account.name,
                    },
                },
                "*",
            );
        } catch (error) {
            console.error("Login error:", error);

            if (error instanceof BrowserAuthError) {
                if (error.errorCode === "popup_window_error") {
                    setError("Popups are blocked. Please allow popups for this site.");
                } else {
                    setError(`Login failed: ${error.message}`);
                }
            } else {
                setError("Login failed. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <Center h="100vh" style={{ background: theme.colors.gray[0] }}>
                <Flex direction="column" align="center" gap="md">
                    <div
                        style={{
                            width: "40px",
                            height: "40px",
                            border: "3px solid #e5e7eb",
                            borderTopColor: theme.colors.purple[6],
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                        }}
                    />
                    <Text c="dimmed" size="sm">
                        Initializing...
                    </Text>
                </Flex>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </Center>
        );
    }

    // Not authenticated - show login UI
    if (!isAuthenticated) {
        return (
            <MsalProvider instance={msalInstance}>
                <Center h="100vh" style={{ background: theme.colors.gray[0], padding: "20px" }}>
                    <Flex
                        direction="column"
                        align="center"
                        gap="md"
                        style={{
                            maxWidth: "300px",
                            width: "100%",
                            padding: "24px",
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Text fw={600} size="lg" c={theme.colors.purple[8]}>
                            CTS Knowledge Search
                        </Text>
                        <Text c="dimmed" size="sm" ta="center">
                            Sign in to access technical documentation
                        </Text>

                        {error && (
                            <Text c="red" size="sm" ta="center">
                                {error}
                            </Text>
                        )}

                        <Button
                            fullWidth
                            onClick={handleLogin}
                            loading={isLoading}
                            variant="gradient"
                            gradient={{ from: "purple.8", to: "purple.6" }}
                        >
                            Sign in with Microsoft
                        </Button>

                        <Text c="dimmed" size="xs" ta="center">
                            This will open a popup window for authentication
                        </Text>
                    </Flex>
                </Center>
            </MsalProvider>
        );
    }

    // Authenticated - render children
    return (
        <MsalProvider instance={msalInstance}>
            <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
                <div style={{ flex: 1, overflow: "hidden" }}>{children}</div>
            </div>
        </MsalProvider>
    );
}
